import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, BookOpen, CalendarDays, Check, ChevronRight, ClipboardList, GraduationCap, Inbox, Library, Pin } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useApi, useFetch, errMsg } from "@/lib/api";
import { today, nextSchoolDay, weekday, fmtLong, fmtShort } from "@/lib/dates";
import { Card, Empty, ErrorBox, Loading, SubjectBadge } from "@/components/common";

const Section = ({ title, icon: Icon, to, linkLabel, children, testId, delay = 0 }) => (
  <section className="fade-up" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="mb-2.5 flex items-center justify-between">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Icon className="h-5 w-5 text-primary" />{title}</h2>
      {to && <Link to={to} className="flex items-center text-sm font-semibold text-primary" data-testid={`${testId}-link`}>{linkLabel}<ChevronRight className="h-4 w-4" /></Link>}
    </div>
    {children}
  </section>
);

export function HomeworkItem({ item, onToggle }) {
  return (
    <button onClick={() => onToggle(item)} className="flex w-full items-start gap-3 rounded-xl p-2 text-left transition-colors hover:bg-secondary/60 active:scale-[0.99]" data-testid={`homework-toggle-${item.id}`}>
      <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-lg border-2 transition-colors ${item.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-border"}`}>
        {item.done && <Check className="h-4 w-4" />}
      </span>
      <span className="min-w-0 flex-1">
        <SubjectBadge subject={item.subject} />
        <span className={`mt-1 block whitespace-pre-line text-sm ${item.done ? "text-muted-foreground line-through" : ""}`}>{item.description}</span>
      </span>
    </button>
  );
}

export function useHomeworkToggle(setter) {
  const api = useApi();
  return async (item) => {
    try {
      const { done } = await api("post", `/homework/${item.id}/done`);
      setter((list) => list.map((h) => (h.id === item.id ? { ...h, done } : h)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
}

const Stat = ({ icon: Icon, label, value, testId }) => (
  <Card className="p-3.5" data-testid={testId}>
    <Icon className="h-5 w-5 text-primary" />
    <p className="mt-2 truncate font-heading text-lg font-bold leading-tight">{value}</p>
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
  </Card>
);

export default function Home() {
  const { me, isAdmin } = useMe();
  const d = today();
  const next = nextSchoolDay(d);
  const wd = weekday(d);
  const { data, error, reload, setData } = useFetch(`/dashboard?date=${d}&next=${next}&weekday=${wd}`);
  const toggle = useHomeworkToggle((fn) => setData((s) => ({ ...s, homework: fn(s.homework) })));
  const first = me?.name?.split(" ")[0];

  if (error) return <ErrorBox text={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const nextTest = data.assessments[0];
  const todo = data.homework.filter((h) => !h.done).length;

  return (
    <div className="space-y-7">
      <div className="fade-up">
        <p className="text-sm font-semibold text-primary" data-testid="home-date">{fmtLong(d)}</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="home-greeting">Ciao, {first}!</h1>
      </div>

      <div className="grid grid-cols-3 gap-2.5 fade-up" style={{ animationDelay: "60ms" }}>
        <Stat icon={ClipboardList} label={`Compiti per ${fmtShort(next).split(" ")[0]}`} value={`${todo} da fare`} testId="stat-homework" />
        <Stat icon={GraduationCap} label={nextTest ? fmtShort(nextTest.date) : "Nessuna in vista"} value={nextTest ? nextTest.subject : "—"} testId="stat-next-test" />
        {isAdmin
          ? <Stat icon={Inbox} label="Messaggi da leggere" value={data.unread_messages} testId="stat-unread" />
          : <Stat icon={CalendarDays} label="Ore oggi" value={wd === 7 ? "—" : data.schedule.length} testId="stat-hours" />}
      </div>

      {data.announcement && (
        <Link to="/bacheca" className="block fade-up" style={{ animationDelay: "100ms" }} data-testid="home-announcement">
          <div className="flex items-start gap-3 rounded-2xl bg-primary p-4 text-primary-foreground shadow-md transition-transform active:scale-[0.99]">
            {data.announcement.pinned ? <Pin className="mt-0.5 h-5 w-5 shrink-0" /> : <BellRing className="mt-0.5 h-5 w-5 shrink-0" />}
            <div className="min-w-0">
              <p className="font-heading font-bold">{data.announcement.title}</p>
              <p className="mt-0.5 line-clamp-2 text-sm opacity-90">{data.announcement.body}</p>
            </div>
          </div>
        </Link>
      )}

      <Link to="/portali" className="flex items-center gap-3 rounded-2xl border border-border/70 bg-card p-4 transition-transform active:scale-[0.99] fade-up" style={{ animationDelay: "120ms" }} data-testid="home-portals-link">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary"><Library className="h-5 w-5" /></span>
        <span className="flex-1"><span className="block font-semibold">Libri digitali</span><span className="block text-xs text-muted-foreground">Portali e credenziali della classe</span></span>
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      </Link>

      <div className="grid gap-7 lg:grid-cols-2">
        <Section title="Orario di oggi" icon={CalendarDays} to="/orario" linkLabel="Settimana" testId="home-schedule" delay={140}>
          {wd === 7 ? <Empty text="Oggi è domenica: niente scuola!" /> : data.schedule.length === 0 ? <Empty text="Orario non ancora inserito." /> : (
            <Card className="space-y-1 p-2">
              {data.schedule.map((s) => (
                <div key={s.id} className="flex items-center gap-3 rounded-xl p-2" data-testid={`home-slot-${s.hour}`}>
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-secondary font-heading text-sm font-bold">{s.hour}ª</span>
                  <div className="min-w-0 flex-1"><SubjectBadge subject={s.subject} />{s.teacher && <p className="mt-0.5 truncate text-xs text-muted-foreground">{s.teacher}{s.room ? ` · ${s.room}` : ""}</p>}</div>
                </div>
              ))}
            </Card>
          )}
        </Section>

        <Section title={`Compiti per ${fmtShort(next)}`} icon={ClipboardList} to="/diario" linkLabel="Diario" testId="home-homework" delay={180}>
          {data.homework.length === 0 ? <Empty text="Nessun compito inserito." /> : (
            <Card className="space-y-1 p-2">{data.homework.map((h) => <HomeworkItem key={h.id} item={h} onToggle={toggle} />)}</Card>
          )}
        </Section>

        <Section title="Argomenti di oggi" icon={BookOpen} testId="home-lessons" delay={220}>
          {data.lessons.length === 0 ? <Empty text="Nessun argomento inserito oggi." /> : (
            <Card className="space-y-3">
              {data.lessons.map((l) => (
                <div key={l.id}><SubjectBadge subject={l.subject} /><p className="mt-1 whitespace-pre-line text-sm">{l.topic}</p></div>
              ))}
            </Card>
          )}
        </Section>

        <Section title="Prossime prove" icon={GraduationCap} to="/verifiche" linkLabel="Tutte" testId="home-assessments" delay={260}>
          {data.assessments.length === 0 ? <Empty text="Nessuna verifica o interrogazione in programma." /> : (
            <Card className="space-y-1 p-2">
              {data.assessments.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl p-2">
                  <div className="min-w-0"><SubjectBadge subject={a.subject} /><p className="mt-0.5 text-sm capitalize">{a.kind}</p></div>
                  <span className="shrink-0 text-sm font-semibold text-muted-foreground">{fmtShort(a.date)}</span>
                </div>
              ))}
            </Card>
          )}
        </Section>
      </div>
    </div>
  );
}
